import React, { useState, useMemo, useCallback } from 'react';
import css from './styles.module.scss';
import { Grid, TextField, Button } from '@material-ui/core';
import Typography from '@material-ui/core/Typography';
import Box from '@material-ui/core/Box';
import { Search } from '~/common/search';
import { DateRange, DateRangeDelimiter, DateRangePicker, LocalizationProvider } from '@material-ui/pickers-next';
import MomentUtils from '@material-ui/pickers-next/adapter/moment';
import { ChipsFilter } from '~/common/chips-filter';
import moment from 'moment';
import { PaginatedBlock } from '~/features/dashboard/components/paginated-block';
import { axiosInstance } from '~/api/config/axios.config';
import { MusicApi } from '~/api/music.api';
import { SongCard } from '~/features/dashboard/components/songs-grid/components/song-card';
import { SongSummary } from '~/features/dashboard/store/models';
import { useDateFormat } from '~/common/date-format';

interface Props {
    showLinked: boolean;
    items: SongSummary[];
    onLink(id: string): void;
    onUnlink(id: string): void;
    onLinkAll(): void;
    onUnlinkAll?(): void;
    itemsPerPage?: number;
}

export const SongRightDrawer: React.FC<Props> = ({ showLinked, items, onLink, onUnlink, onLinkAll, onUnlinkAll, itemsPerPage = 10 }) => {
    const { selectedDateFormat } = useDateFormat();
    const [songStatusFilter, setSongStatusFilter] = useState<string[]>([]);
    const [dealStatusFilter, setDealStatusFilter] = useState<string[]>([]);
    const [dateFilter, setDateFilter] = React.useState<DateRange<Date>>([null, null]);
    const [searchValue, setSearchValue] = useState('');

    const statuses = useMemo(() => {
        const songStatuses: string[] = [];
        items.forEach(item => {
            if (item.showSongStatus.trim() && !songStatuses.includes(item.showSongStatus)) {
                songStatuses.push(item.showSongStatus);
            }
        });
        return songStatuses;
    }, [items]);

    const dealStatuses = useMemo(() => {
        const tmpDealStatuses: string[] = [];
        items.forEach(item => {
            if (item.showSongDealStatus.trim() && !tmpDealStatuses.includes(item.showSongDealStatus)) {
                tmpDealStatuses.push(item.showSongDealStatus);
            }
        });
        return tmpDealStatuses;
    }, [items]);

    const filteredItems: SongSummary[] = useMemo(() => {
        const [minDate, maxDate] = dateFilter;
        if (songStatusFilter.length || dealStatusFilter.length || searchValue || (minDate && maxDate)) {
            return items.filter(item => {
                if (songStatusFilter.length && !songStatusFilter.includes(item.showSongStatus)) {
                    return false;
                }
                if (dealStatusFilter.length && !dealStatusFilter.includes(item.showSongDealStatus)) {
                    return false;
                }
                if (searchValue) {
                    const title = item.songName.toLowerCase();
                    const search = searchValue.toLowerCase();
                    return title.includes(search);
                }
                if (minDate && maxDate && item.releaseDate) {
                    const compareDate = moment(item.releaseDate);
                    compareDate.isSameOrBefore(maxDate) && compareDate.isSameOrAfter(minDate);
                }
                return true;
            });
        }
        return items;
    }, [items, searchValue, dateFilter, songStatusFilter, dealStatusFilter]);

    return (
        <Grid container className={css.module} spacing={3}>
            <Grid item xs={12}>
                <Box display="flex" justifyContent="space-between">
                    <Typography variant="h6" gutterBottom>
                        {showLinked ? 'VIEW SONGS' : 'ADD SONGS'}
                    </Typography>
                    {showLinked ? (
                        <Button
                            className={css.btnColorGreen}
                            variant="contained"
                            color="secondary"
                            style={{ height: '2rem', borderRadius: '8px' }}
                            onClick={onUnlinkAll}
                        >
                            Unlink All
                        </Button>
                    ) : (
                        <Button
                            className={css.btnColorGreen}
                            variant="contained"
                            color="secondary"
                            style={{ height: '2rem', borderRadius: '8px' }}
                            onClick={onLinkAll}
                        >
                            Link All
                        </Button>
                    )}
                </Box>
            </Grid>

            <Grid item xs={6}>
                {statuses.length > 1 && (
                    <Box pb={2}>
                        <Box pb={1}>
                            <Typography variant="body2" className={css.bold}>
                                Song Status filters:
                            </Typography>
                        </Box>
                        <ChipsFilter chips={statuses} filters={songStatusFilter} onFilterChange={setSongStatusFilter} className={css.chipFilter} />
                    </Box>
                )}
                {dealStatuses.length > 1 && (
                    <Box pb={2}>
                        <Box pb={1}>
                            <Typography variant="body2" className={css.bold}>
                                Deal status filters:
                            </Typography>
                        </Box>
                        <ChipsFilter chips={dealStatuses} filters={dealStatusFilter} onFilterChange={setDealStatusFilter} className={css.chipFilter} />
                    </Box>
                )}
            </Grid>

            {items.length > itemsPerPage && (filteredItems.length > itemsPerPage || searchValue || dateFilter[0] || dateFilter[1]) && (
                <Grid item xs={6}>
                    <Box display="flex" flexDirection="column" justifyContent="center">
                        <Box pb={2}>
                            <LocalizationProvider dateAdapter={MomentUtils}>
                                <DateRangePicker
                                    className={css.picker}
                                    startText="Date from"
                                    endText="Date to"
                                    value={dateFilter}
                                    inputFormat={selectedDateFormat}
                                    onChange={newValue => setDateFilter(newValue)}
                                    renderInput={(startProps, endProps) => (
                                        <>
                                            <TextField {...startProps} className={css.datePickerInput} variant="standard" />
                                            <DateRangeDelimiter> to </DateRangeDelimiter>
                                            <TextField {...endProps} className={css.datePickerInput} variant="standard" />
                                        </>
                                    )}
                                />
                            </LocalizationProvider>
                        </Box>
                        <Box pb={2}>
                            <Search search={searchValue} onSearchChange={setSearchValue} className={css.search} />
                        </Box>
                    </Box>
                </Grid>
            )}

            <Grid item xs={12}>
                <PaginatedBlock
                    grid={2}
                    keyName="assetCode"
                    data={filteredItems}
                    renderItem={(item, key) => (
                        <SongCard
                            className={css.item}
                            data={item}
                            key={key}
                            onChange={(isChecked): void => {isChecked ? onLink(item.assetCode) : onUnlink(item.assetCode)}}
                        />
                    )}
                />
            </Grid>
        </Grid>
    );
};

type ConnectedProps = Omit<Props, 'onLink' | 'onUnlink' | 'onLinkAll' | 'onUnlinkAll'> & {
    assetCode: string;
    projectId: number;
    onClose(): void;
};

const ConnectedSongRightDrawer: React.FC<ConnectedProps> = ({ assetCode, projectId, onClose, ...props }) => {
    const onLink = useCallback(
        (id: string): void => {
            MusicApi.linkDeal(id, projectId, assetCode);
            // axiosInstance
            //     .post(`catalogue/projectCreator/asset/link?projectId=${projectId}&assetCode=${assetCode}&songRef=${id}`);
        },
        [assetCode, projectId]
    );

    const onUnlink = useCallback(
        (id: string): void => {
            MusicApi.unlinkDeal(id, projectId, assetCode);
            // axiosInstance
            //     .post(`catalogue/projectCreator/asset/unlink?projectId=${projectId}&assetCode=${assetCode}&songRef=${id}`);
        },
        [assetCode, projectId]
    );

    const linkAll = useCallback((): void => {
        axiosInstance.post(`catalogue/projectCreator/asset/link?projectId=${projectId}&assetCode=${assetCode}&all=true`).then(() => {
            onClose();
        });
    }, [projectId, assetCode, onClose]);

    const unlinkAll = useCallback(() => {
        axiosInstance.post(`catalogue/projectCreator/asset/unlink?projectId=${projectId}&assetCode=${assetCode}&all=true`).then(() => {
            onClose();
        });
    }, [projectId, assetCode, onClose]);

    return <SongRightDrawer {...props} onLink={onLink} onUnlink={onUnlink} onLinkAll={linkAll} onUnlinkAll={unlinkAll} />;
};

export default ConnectedSongRightDrawer;
